import { useEffect, useState, startTransition } from "react";
import { Link } from "react-router-dom";
import { Star, Quote } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { propertyUrl, tr } from "@/lib/translations";

const initials = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join("");
const shortName = (name = "") => { const [f, ...r] = name.split(" "); return r.length ? `${f} ${r[r.length - 1][0]}.` : f; };

export const FeaturedReviews = ({ limit = 3 }) => {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/reviews/featured", { params: { limit } }).then((r) => startTransition(() => setItems(r.data))).catch(() => {}); }, [limit]);
  if (!items.length) return null;

  return (
    <section className="bg-tint home-section" data-testid="featured-reviews">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="home-section-head">
          <div className="text-xs uppercase tracking-[0.3em] text-brand flex items-center gap-2"><Star className="w-4 h-4 fill-gold text-gold" />{t.reviews.subtitle}</div>
          <h3 className="font-serif text-4xl md:text-5xl text-stone-900 mt-3">{t.reviews.title}</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((r, i) => (
            <article key={r.id} className="bg-white rounded-2xl border border-stone-200/60 p-6 flex flex-col shadow-sm hover:shadow-lg transition-shadow min-w-0" style={{ animationDelay: `${i * 100}ms` }} data-testid={`featured-review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5" role="img" aria-label={`${r.rating}/5`} data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-gold text-gold" : "text-stone-300"}`} />)}
                </div>
                <Quote className="w-6 h-6 text-line" />
              </div>
              <p className="text-stone-700 leading-relaxed mt-4 flex-1 line-clamp-5">„{r.translations?.[lang] || r.text}"</p>
              {r.translations?.[lang] && r.source_lang && r.source_lang !== lang && <span className="text-[10px] text-stone-400 mt-2">{t.reviews.autoTranslated}</span>}
              <div className="flex items-center gap-3 mt-6 pt-5 border-t border-stone-100">
                <div className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center text-sm font-semibold">{initials(r.guest_name)}</div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-stone-900">{shortName(r.guest_name)}</div>
                  <Link to={propertyUrl(r.property)} className="text-xs text-brand hover:underline truncate block" data-testid={`review-property-link-${r.property.slug}`}>
                    {tr(r.property.title, lang)}
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
